import {
    memo,
    useEffect,
    useMemo,
    useState,
    type ComponentType,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';

import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { PASSWORD_CHANGED_NOTICE } from '@xver/hooks/useLogout';
import usePageHeading from '@xver/hooks/usePageHeading';
import { resetAuthPageState } from '@xver/store/authPage/authPage.actions';
import { LandingLayout } from '../../../layouts';
import PhoneNumberVerificationForm from './components/PhoneNumberVerificationForm/PhoneNumberVerificationForm';
import ResetPasswordForm from './components/ResetPasswordForm/ResetPasswordForm';
import SignInForm from './components/SignInForm/SignInForm';
import SignUpForm from './components/SignUpForm/SignUpForm';
import TwoFactorVerificationForm from './components/TwoFactorVerificationForm/TwoFactorVerificationForm';
import { PAGE_TYPE, type PageType } from './constants';

interface PageProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

const PAGES: Record<PageType, ComponentType<PageProps>> = {
    [PAGE_TYPE.SIGN_IN]: SignInForm,
    [PAGE_TYPE.SIGN_UP]: SignUpForm,
    [PAGE_TYPE.TWO_FACTOR_VERIFICATION]: TwoFactorVerificationForm,
    [PAGE_TYPE.PHONE_NUMBER_VERIFICATION]: PhoneNumberVerificationForm,
    [PAGE_TYPE.RESET_PASSWORD]: ResetPasswordForm,
};

function Authentication() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('auth');

    const [pageType, setPageType] = useState<PageType>(PAGE_TYPE.SIGN_IN);

    const pageTitles: Record<PageType, string> = {
        [PAGE_TYPE.SIGN_IN]: t('signIn.title'),
        [PAGE_TYPE.SIGN_UP]: t('signUp.title'),
        [PAGE_TYPE.TWO_FACTOR_VERIFICATION]: t('twoFactor.title'),
        [PAGE_TYPE.PHONE_NUMBER_VERIFICATION]: t('phoneVerification.title'),
        [PAGE_TYPE.RESET_PASSWORD]: t('resetPassword.title'),
    };
    usePageHeading(pageTitles[pageType]);

    const Page = useMemo(() => PAGES[pageType], [pageType]);

    useEffect(() => {
        return () => {
            dispatch(resetAuthPageState());
        };
    }, [dispatch]);

    useEffect(() => {
        const setIn = sessionStorage.getItem(PASSWORD_CHANGED_NOTICE);
        // The outgoing document also mounts this page before its reload lands; only the new one shows it.
        if (!setIn || setIn === String(performance.timeOrigin)) return;
        sessionStorage.removeItem(PASSWORD_CHANGED_NOTICE);
        notifications.show({
            title: t('security:toast.passwordChangedTitle'),
            message: t('security:toast.passwordChangedMessage'),
            icon: <IconCheck size={18} color="#22c55e" />,
        });
    }, [t]);

    return (
        <LandingLayout>
            <Page setPageType={setPageType} />
        </LandingLayout>
    );
}

export default memo(Authentication);
